import { useEffect, useState } from "react";
import { API_GET_PROJECTS_BY_EMPLOYEE_ID, GET_EMPLOYEE_PROFILE_BY_EMPID } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import { getApi } from "../../utils/api.ts";
import { currentProjects, type CurrentProject, type EmployeeProject } from "../growth-activities/contribution/contribution-data.ts";

type Profile = { id: number; designation?: string; department?: string; managerName?: string; techStack?: string };
type AssignmentProject = EmployeeProject & { dlName?: string | null };
export default function useEmployeeContext(employeeId?: number) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [projects, setProjects] = useState<(CurrentProject & { deliveryLead: string | null })[]>([]);
  const [projectNames, setProjectNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [projectsError, setProjectsError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!employeeId) return;
    const controller = new AbortController();
    setLoading(true); setProfile(null); setProjects([]); setProjectNames({}); setProfileError(false); setProjectsError(false);
    const token = getAuthToken("accessToken");
    void Promise.allSettled([
      getApi<{ data: { personalDetail: Profile } }>(`${GET_EMPLOYEE_PROFILE_BY_EMPID}/${employeeId}`, token, "json", controller.signal),
      getApi<{ data: { projects: AssignmentProject[] } }>(`${API_GET_PROJECTS_BY_EMPLOYEE_ID}/${employeeId}`, token, "json", controller.signal),
    ]).then(([person, assignments]) => {
      if (controller.signal.aborted) return;
      if (person.status === "fulfilled" && person.value.data?.data?.personalDetail?.id === employeeId) {
        // Keep only the work context needed by this page, excluding other personal/profile fields.
        const { id, designation, department, managerName, techStack } = person.value.data.data.personalDetail;
        setProfile({ id, designation, department, managerName, techStack });
      } else setProfileError(true);
      if (assignments.status === "fulfilled" && Array.isArray(assignments.value.data?.data?.projects)) {
        const allProjects = assignments.value.data.data.projects;
        setProjectNames(Object.fromEntries(allProjects.filter((project) => project.projectName?.trim()).map((project) => [project.projectId, project.projectName.trim()])));
        const leads = new Map(allProjects.map((project) => [project.projectId, project.dlName?.trim() || null]));
        setProjects(currentProjects(allProjects, employeeId).map((project) => ({ ...project, deliveryLead: leads.get(project.id) || null })));
      } else setProjectsError(true);
      setLoading(false);
    });
    return () => controller.abort();
  }, [employeeId, attempt]);
  return { profile, projects, projectNames, loading, profileError, projectsError, retry: () => setAttempt((value) => value + 1) };
}
